import React, { useEffect, useState, useMemo } from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import { useNavigate } from "react-router-dom";
import {
  fetchPortalPermitApps,
  fetchPortalCheckApps,
  fetchPortalSpectApps,
  fetchPortalPermitApp,
  fetchPortalCheckApp,
  fetchPortalSpectApp,
  updatePortalPermitApp,
  updatePortalCheckApp,
  updatePortalSpectApp,
  downloadPortalPermitAppPdf
} from "../../services/portalService";
import Table from "../../utils/Table";
import { getStatusDot } from "../../utils/TableUtils";
import { useToast } from "../../contexts/ToastContext";
import ApplicationModal from "../../components/Modals/ApplicationModal";
import CheckApplicationModal from "../../components/Modals/CheckApplicationModal";
import SpectApplicationModal from "../../components/Modals/SpectApplicationModal";
import editIcon from "../../assets/images/icons/edit.svg";
import downlaodIcon from "../../assets/images/icons/download.svg";
import viewIcon from "../../assets/images/icons/view.svg";

function PortalDashboard({ product = "permit" }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [filterInput, setFilterInput] = useState("");
  
  // Modal states
  const [selectedApp, setSelectedApp] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const navigate = useNavigate();
  const { showToast } = useToast();

  const pageSize = 10;

  useEffect(() => {
    loadDashboard();
  }, [product]);

  // Reset page index when switching products
  useEffect(() => {
    setPageIndex(0);
  }, [product]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError("");
      
      let res;
      if (product === "permit") {
        res = await fetchPortalPermitApps();
      } else if (product === "check") {
        res = await fetchPortalCheckApps();
      } else if (product === "spect") {
        res = await fetchPortalSpectApps();
      } else {
        throw new Error("Invalid product type");
      }

      setApplications(res.data || []);
    } catch (err) {
      setError(err.message || "Failed to load applications.");
      showToast("Error loading applications", "error");
    } finally {
      setLoading(false);
    }
  };

  // Filter and search applications based on product and search input
  const filteredApps = useMemo(() => {
    if (!applications) return [];

    // Sort newest first
    const sorted = [...applications].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    if (!filterInput) return sorted;
    const lower = filterInput.toLowerCase();
    return sorted.filter(app => {
      const name = app.product === "permit" 
        ? `${app.firstName || ""} ${app.lastName || ""}` 
        : (app.name || "");
      return (
        name.toLowerCase().includes(lower) ||
        (app.status || "").toLowerCase().includes(lower) ||
        (app.applicantId || app.id || "").toString().includes(lower)
      );
    });
  }, [applications, filterInput]);

  // Client-side pagination slice
  const pageCount = Math.ceil(filteredApps.length / pageSize);
  const paginatedApps = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredApps.slice(start, start + pageSize);
  }, [filteredApps, pageIndex]);

  const handleEditClick = async (app) => {
    const id = app.applicantId || app.id;
    try {
      setModalLoading(true);
      let res;
      if (app.product === "permit" || product === "permit") {
        res = await fetchPortalPermitApp(id);
      } else if (app.product === "check" || product === "check") {
        res = await fetchPortalCheckApp(id);
      } else if (app.product === "spect" || product === "spect") {
        res = await fetchPortalSpectApp(id);
      }
      if (res && res.application) {
        setSelectedApp({ ...res.application, product: app.product || product });
        setModalShow(true);
      } else {
        showToast("Failed to load application details", "error");
      }
    } catch (err) {
      showToast(err.message || "Failed to load application details", "error");
    } finally {
      setModalLoading(false);
    }
  };

  const handleSavePermit = async (formData) => {
    const id = selectedApp.applicantId || selectedApp.id;
    const payload = {
      applicant: formData.applicant,
      contractors: formData.contractors,
      location: formData.location,
    };
    await updatePortalPermitApp(id, payload);
    loadDashboard();
  };

  const handleSaveCheck = async (combinedData) => {
    const id = selectedApp.id || selectedApp.applicantId;
    const payload = {
      name: combinedData.name,
      address: combinedData.address,
      phoneNumber: combinedData.phoneNumber,
      comments: combinedData.comments,
    };
    await updatePortalCheckApp(id, payload);
    loadDashboard();
  };

  const handleSaveSpect = async (combinedData) => {
    const id = selectedApp.id || selectedApp.applicantId;
    const payload = {
      name: combinedData.name,
      address: combinedData.address,
      phoneNumber: combinedData.phoneNumber,
      comments: combinedData.comments,
      requestedInspection: combinedData.requestedInspection,
    };
    await updatePortalSpectApp(id, payload);
    loadDashboard();
  };

  const handleDownloadClick = async (app) => {
    const id = app.applicantId || app.id;
    try {
      showToast("Generating PDF...", "info");
      await downloadPortalPermitAppPdf(id);
      showToast("Download started", "success");
    } catch (err) {
      showToast(err.message || "Failed to download PDF", "error");
    }
  };

  // Columns definition matching the admin dashboard columns
  const columns = useMemo(() => [
    {
      Header: "Date Created",
      accessor: "createdAt",
      Cell: ({ value }) => {
        if (!value) return "N/A";
        return new Date(value).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    },
    {
      Header: "Application #",
      accessor: (row) => row.applicantId || row.id,
      Cell: ({ value }) => value?.toString().padStart(2, "0")
    },
    {
      Header: "Applicant Name",
      accessor: (row) => row.product === "permit" ? `${row.firstName || ""} ${row.lastName || ""}` : (row.name || "N/A"),
      Cell: ({ row }) => {
        const app = row.original;
        return app.product === "permit" ? `${app.firstName || ""} ${app.lastName || ""}` : (app.name || "N/A");
      }
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => getStatusDot(value)
    },
    {
      Header: "Actions",
      accessor: "actions",
      disableSortBy: true,
      Cell: ({ row }) => {
        const app = row.original;
        const id = app.applicantId || app.id;
        return (
          <div className="d-flex align-items-center">
            <button
              onClick={() => navigate(`/portal/application/${app.product}/${id}`)}
              title="View Details"
              className="btn btn-outline-secondary btn-sm"
              style={{ padding: "5px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            >
              <img 
                src={viewIcon} 
                alt="View Details" 
                style={{ width: "20px", height: "20px" }} 
              />
            </button>
            {app.editable && (
              <button
                onClick={() => handleEditClick(app)}
                disabled={modalLoading}
                title="Edit"
                className="btn btn-outline-secondary btn-sm"
                style={{ marginLeft: "5px", padding: "5px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <img 
                  src={editIcon} 
                  alt="Edit" 
                  style={{ width: "20px", height: "20px" }} 
                />
              </button>
            )}
            {app.product === "permit" && (
              <button
                onClick={() => handleDownloadClick(app)}
                title="Download PDF"
                className="btn btn-outline-secondary btn-sm"
                style={{ marginLeft: "5px", padding: "5px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <img 
                  src={downlaodIcon} 
                  alt="Download" 
                  style={{ width: "20px", height: "20px" }} 
                />
              </button>
            )}
          </div>
        );
      }
    }
  ], [navigate, modalLoading, product]);

  // Map product to page heading title
  const getPageTitle = () => {
    if (product === "permit") return "Simplepermit ™ Applications";
    if (product === "check") return "Simplecheck ™ Plan Review Requests";
    if (product === "spect") return "Simplespect ™ Inspection Requests";
    return "My Submissions";
  };

  return (
    <div className="subMain-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mt-4">{getPageTitle()}</h1>
        </div>
        <div>
          <Button variant="outline-secondary" size="sm" onClick={loadDashboard}>
            Refresh
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Table
        columns={columns}
        data={paginatedApps}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
        onPageChange={(idx) => setPageIndex(idx)}
        currentPage={pageIndex}
        pageCount={pageCount}
        loading={loading}
      />

      {/* Edit Modals */}
      {selectedApp && selectedApp.product === "permit" && (
        <ApplicationModal
          show={modalShow && selectedApp.product === "permit"}
          onHide={() => {
            setModalShow(false);
            setSelectedApp(null);
          }}
          data={selectedApp}
          isPortalUser={true}
          onSave={handleSavePermit}
        />
      )}
      {selectedApp && selectedApp.product === "check" && (
        <CheckApplicationModal
          show={modalShow && selectedApp.product === "check"}
          onHide={() => {
            setModalShow(false);
            setSelectedApp(null);
          }}
          data={selectedApp}
          isPortalUser={true}
          onSave={handleSaveCheck}
        />
      )}
      {selectedApp && selectedApp.product === "spect" && (
        <SpectApplicationModal
          show={modalShow && selectedApp.product === "spect"}
          onHide={() => {
            setModalShow(false);
            setSelectedApp(null);
          }}
          data={selectedApp}
          isPortalUser={true}
          onSave={handleSaveSpect}
        />
      )}
    </div>
  );
}

export default PortalDashboard;
